import React, { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { adminLogin, verifyAdminToken } from "../api/admin/adminAuth";

interface AdminAuthContextType {
  adminToken: string | null;
  isAuthenticated: boolean;
  login: (password: string) => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export const AdminAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [adminToken, setAdminToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (token) {
      verifyAdminToken(token)
        .then(() => {
          setAdminToken(token);
          setIsLoading(false);
        })
        .catch(() => {
          localStorage.removeItem("admin_token");
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = async (password: string): Promise<boolean> => {
    try {
      const { accessToken } = await adminLogin(password);
      setAdminToken(accessToken);
      localStorage.setItem("admin_token", accessToken);
      return true;
    } catch {
      return false;
    }
  };

  const logout = () => {
    setAdminToken(null);
    localStorage.removeItem("admin_token");
    window.location.href = "/admin";
  };

  return (
    <AdminAuthContext.Provider
      value={{
        adminToken,
        isAuthenticated: !!adminToken,
        login,
        logout,
        isLoading,
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error("useAdminAuth must be used within AdminAuthProvider");
  }
  return context;
};
